import { useCallback, useState } from 'react';
import type { PointerEvent } from 'react';
import { ACTIVE_FALLBACK_LOOK, PRESSED_LOOK } from './pressedState';

/**
 * Controls inside a pressable element that act on their own (a card's details
 * toggle, Edit, Delete). Pressing one of them does not press the element
 * around it.
 */
export const INNER_CONTROL_SELECTOR = 'button, a, input, [role="button"], [data-card-control]';

/** True when the press started on the element itself, not on a control inside it. */
export function pressStartedOnElement(element: Element, target: Element): boolean {
  const control = target.closest(INNER_CONTROL_SELECTOR);
  return control === null || control === element || !element.contains(control);
}

/** A disabled or inert element takes no press. */
export function acceptsPress(element: Element): boolean {
  return (
    !element.matches(':disabled') &&
    element.getAttribute('aria-disabled') !== 'true' &&
    element.closest('[inert]') === null
  );
}

export type PressHandlers = {
  onPointerDown: (event: PointerEvent<Element>) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onPointerLeave: () => void;
};

/**
 * A visible pressed state for a clickable with no ripple of its own (a plain
 * Box, a Link, a button, an icon). The app turns the browser's tap highlight
 * off everywhere, so without this a tap on one of them showed nothing until
 * whatever it does appeared.
 *
 * It is tracked from pointer events, not CSS `:active`:
 * - Chrome on a touch screen does not apply `:active` while a finger is held
 *   down, so a held press showed nothing;
 * - `:active` also matches every ancestor of the pressed element, so pressing
 *   a control inside a card faded the whole card, and `:has()`, which could
 *   exclude it, is missing from the oldest web views the apps run in (iOS
 *   15.0-15.3).
 *
 * The press ends on pointer up, cancel (a scroll) or leave. Spread
 * `pressHandlers` on the element and add `pressedSx` last in its `sx`.
 * An element with controls inside it (a card) passes
 * `{ activeFallback: false }`, so the CSS fallback cannot fade it when one of
 * those controls is pressed.
 */
export function usePressedState(
  enabled = true,
  { activeFallback = true }: { activeFallback?: boolean } = {},
): {
  pressedSx: typeof PRESSED_LOOK | typeof ACTIVE_FALLBACK_LOOK | Record<string, never>;
  pressHandlers: PressHandlers;
} {
  const [pressed, setPressed] = useState(false);
  const release = useCallback(() => setPressed(false), []);
  const onPointerDown = useCallback(
    (event: PointerEvent<Element>) => {
      // A secondary button (right or middle click) is not a press; touch and
      // pen contact report the primary button, 0.
      if (event.button > 0) return;
      const { currentTarget, target } = event;
      setPressed(
        enabled &&
          target instanceof Element &&
          acceptsPress(currentTarget) &&
          pressStartedOnElement(currentTarget, target),
      );
    },
    [enabled],
  );
  return {
    pressedSx: !enabled ? {} : pressed ? PRESSED_LOOK : activeFallback ? ACTIVE_FALLBACK_LOOK : {},
    pressHandlers: {
      onPointerDown,
      onPointerUp: release,
      onPointerCancel: release,
      onPointerLeave: release,
    },
  };
}
